import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { SelectItem } from "@heroui/react";
import type { AxiosError } from "axios";
import { getOptionsBySource, uploadMediaFile } from "../../../services/api";
import AppSelect from "../../../components/common/appSelect";

import {
  PreviewValues,
  normalizeOptions,
  setByPath,
  getByPath,
  isGroupLike,
  LabelRow,
} from "./opportunityFormCommon";
import type { SelectOption, RequiredFieldIssue, TemplateFieldLike, TemplateStepLike, OnChangeRule, EventOpportunityDefaults, ReviewSummaryField, ReviewSummaryStep } from "./opportunityFormTypes";

import {
  CityDropdown,
  MultiSelectDropdown,
  DurationInput,
  FighterSearchInput,
  CountryDropdown,
} from "./opportunityFormComponents";
import {
  PERCENT_OF_BASE_MODE,
  getComponentAmount,
  getOutcomePolicyFormValue,
  getOutcomeRegistryRows,
  type PayoutComponent,
} from "../../../utils/opportunities/outcomeRegistryHelpers";
import { resolveDateFieldBounds } from "../../../utils/deals/dateFieldRules";
import {
  getDisabledUniqueAcrossItemsValues,
  getUniqueAcrossItemsContextFromPath,
  getUniqueAcrossItemsError,
} from "../../../utils/common/uniqueAcrossItems";

import {
  DATE_MAX_FOUR_DIGIT_YEAR,
  flattenTranslationValues,
  formatOptionLabelForUi,
  formatOptionLabelsForUi,
  getEventDefaultForField,
  isFourDigitYearDate,
  normalizeOptionsSource,
} from "./opportunityFormHelpers";
import { ReviewStep } from "./formSteps/reviewStep";
import { OutcomeRegistryStep } from "./formSteps/outcomeRegistryStep";
import { RepeatableStep } from "./formSteps/repeatableStep";

export type { EventOpportunityDefaults };

export interface PanelProps {
  steps: TemplateStepLike[];
  allSteps: TemplateStepLike[];
  previewValues: PreviewValues;
  setPreviewValues: React.Dispatch<React.SetStateAction<PreviewValues>>;
  loading: boolean;
  isEditMode: boolean;
  templateName?: string;
  onSubmit: () => Promise<void>;
  onClose?: () => void;
  resolveOptionsFrom: (
    optionsFrom: string,
  ) => { label: string; value: string }[];
  eventDefaults?: EventOpportunityDefaults;
  headerAction?: React.ReactNode;
}

const OpportunityForm: React.FC<PanelProps> = ({
  steps,
  previewValues,
  setPreviewValues,
  loading,
  isEditMode,
  templateName,
  onSubmit,
  onClose,
  resolveOptionsFrom,
  eventDefaults,
  headerAction,
}) => {
  const { t, i18n } = useTranslation("CreateEditOpportunities");

  const enBundle =
    i18n.getResourceBundle("en", "CreateEditOpportunities") || {};
  const marketBundle = i18n.getResourceBundle("en", "marketPlaceCards") || {};
  const reverseTranslationMap = {
    ...flattenTranslationValues(enBundle),
    ...flattenTranslationValues(marketBundle),
  };

  const translateFieldText = (
    key?: string,
    fallback: string = "",
  ): string => {
    if (!key) return fallback;

    const direct = t(key);
    if (direct !== key) return direct;

    const mappedKey = reverseTranslationMap[key];
    if (mappedKey) {
      const mapped = t(mappedKey);
      if (mapped !== mappedKey) return mapped;

      const createEditMapped = i18n.t(mappedKey, { ns: "CreateEditOpportunities" });
      if (createEditMapped !== mappedKey) return createEditMapped;

      const marketMapped = i18n.t(mappedKey, { ns: "marketPlaceCards" });
      if (marketMapped !== mappedKey) return marketMapped;
    }

    return key;
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [uploadingFields, setUploadingFields] = useState<
    Record<string, boolean>
  >({});

  const [uploadedFileNames, setUploadedFileNames] = useState<
    Record<string, string>
  >({});

  const [attemptedStepIndexes, setAttemptedStepIndexes] = useState<Set<number>>(
    () => new Set(),
  );

  const [touchedPaths, setTouchedPaths] = useState<Set<string>>(
    () => new Set(),
  );

  const [optionsBySource, setOptionsBySource] = useState<
    Record<string, SelectOption[]>
  >({});

  const [loadingOptionsBySource, setLoadingOptionsBySource] = useState<
    Record<string, boolean>
  >({});

  const tabsRef = useRef<HTMLDivElement | null>(null);

  const optionSources: string[] = (() => {
    const sources = new Set<string>();
    const collectFieldSources = (fields: TemplateFieldLike[] = []) => {
      fields.forEach((field) => {
        const source = normalizeOptionsSource(field?.optionsSource);
        if (source) sources.add(source);
        if (Array.isArray(field?.fields)) collectFieldSources(field.fields);
      });
    };

    steps.forEach((step) => collectFieldSources(step?.fields || []));
    return Array.from(sources);
  })();

  const optionSourcesKey = optionSources.join("|");

  useEffect(() => {
    setCurrentStepIndex(0);
    setAttemptedStepIndexes(new Set());
    setTouchedPaths(new Set());
  }, [steps.length]);

  useEffect(() => {
    const sourcesToLoad = optionSourcesKey.split("|").filter(Boolean);

    if (sourcesToLoad.length === 0) return;

    let cancelled = false;

    setLoadingOptionsBySource((prev) => {
      const next = { ...prev };
      sourcesToLoad.forEach((source) => {
        next[source] = true;
      });
      return next;
    });

    sourcesToLoad.forEach(async (source) => {
      try {
        const values = await getOptionsBySource(`/${source}`);
        if (cancelled) return;
        setOptionsBySource((prev) => ({
          ...prev,
          [source]: normalizeOptions(values),
        }));
      } catch (error: unknown) {
        if (!cancelled) {
          const errMessage =
            error instanceof Error
              ? error.message
              : typeof error === "string"
                ? error
                : `Failed to load options for ${source.split("/").pop()}`;
          toast.error(errMessage);
        }
      } finally {
        if (!cancelled) {
          setLoadingOptionsBySource((prev) => ({
            ...prev,
            [source]: false,
          }));
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [optionSourcesKey]);

  const getOptionsForField = (field: TemplateFieldLike): SelectOption[] => {
    const source = normalizeOptionsSource(field?.optionsSource);

    if (source) {
      return formatOptionLabelsForUi(optionsBySource[source] || []);
    }

    const options = normalizeOptions(field?.options || []);
    const translatedOptions = options.map((opt: SelectOption) => ({
      ...opt,
      label: opt.label ? translateFieldText(opt.label) : opt.label,
    }));
    return formatOptionLabelsForUi(translatedOptions);
  };

  const isFieldOptionsLoading = (field: TemplateFieldLike): boolean => {
    const source = normalizeOptionsSource(field?.optionsSource);
    return !!(source && loadingOptionsBySource[source]);
  };

  useEffect(() => {
    const tabsElement = tabsRef.current;
    if (!tabsElement) return;
    const activeTab = tabsElement.querySelector(
      `[data-step-index="${currentStepIndex}"]`,
    ) as HTMLElement | null;
    activeTab?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentStepIndex, steps.length]);

  const markTouched = (path: string) => {
    setTouchedPaths((prev) => {
      if (prev.has(path)) return prev;
      return new Set(prev).add(path);
    });
  };

  const clearTouchedUnder = (prefix: string) => {
    setTouchedPaths(
      (prev) => new Set(Array.from(prev).filter((path) => !path.startsWith(prefix))),
    );
  };

  const resetAttemptedForCurrentStep = () => {
    setAttemptedStepIndexes((prev) => {
      const next = new Set(prev);
      next.delete(currentStepIndex);
      return next;
    });
  };

  const isErrorShown = (fullPath: string): boolean =>
    attemptedStepIndexes.has(currentStepIndex) || touchedPaths.has(fullPath);

  const updateValue = (
    path: string,
    value: unknown,
    onChangeDefs?: OnChangeRule[],
  ) => {
    markTouched(path);
    setPreviewValues((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      setByPath(next, path, value);

      if (path.endsWith("componentType")) {
        if (next.payoutPolicies) {
          next.payoutPolicies.winComponents = [];
          next.payoutPolicies.lossComponents = [];
        }

        steps.forEach((step) => {
          if (!step?.stepId || !step?.outcomeRegistry) return;

          getOutcomeRegistryRows(step.outcomeRegistry).forEach((row) => {
            setByPath(
              next,
              `${step.stepId}.outcomes.${row.key}.selectedComponents`,
              [],
            );
          });
        });
      }

      if (onChangeDefs && Array.isArray(onChangeDefs)) {
        for (const action of onChangeDefs) {
          if (action.action === "reset" && action.target) {
            const pathParts = path.split(".");
            pathParts[pathParts.length - 1] = action.target;
            setByPath(next, pathParts.join("."), null);
          }
        }
      }

      return next;
    });
  };

  const resolveCountryForCity = (parentPath: string): string => {
    const siblingCountry = getByPath(previewValues, `${parentPath}.country`) as
      | string
      | undefined;
    if (siblingCountry) return siblingCountry;
    for (const step of steps) {
      const countryInStep = getByPath(previewValues, `${step.stepId}.country`) as
        | string
        | undefined;
      if (countryInStep) return countryInStep;
    }
    return "";
  };

  const evaluateVisibleWhen = (
    visibleWhen: Record<string, unknown>,
    parentPath: string,
  ) => {
    if (!visibleWhen) return true;
    const conditionEntry = Object.entries(visibleWhen)[0] as [string, unknown];
    const dependencyKey = conditionEntry[0];
    const expectedValue = conditionEntry[1];
    const siblingValue = getByPath(previewValues, `${parentPath}.${dependencyKey}`);
    const absoluteValue = getByPath(previewValues, dependencyKey);
    let crossStepValue: unknown = undefined;
    if (siblingValue === undefined && absoluteValue === undefined) {
      for (const step of steps) {
        const stepValue = getByPath(previewValues, `${step.stepId}.${dependencyKey}`);
        if (stepValue !== undefined) {
          crossStepValue = stepValue;
          break;
        }
      }
    }
    const currentValue =
      siblingValue !== undefined
        ? siblingValue
        : absoluteValue !== undefined
          ? absoluteValue
          : crossStepValue;
    if (Array.isArray(expectedValue)) return expectedValue.includes(currentValue);
    return currentValue === expectedValue;
  };

  const isFieldVisible = (field: TemplateFieldLike, parentPath: string) => {
    if (!field.visibleWhen) return true;
    return evaluateVisibleWhen(field.visibleWhen, parentPath);
  };

  const resolveMax = (
    field: TemplateFieldLike,
    parentPath: string,
  ): number | undefined => {
    if (!field.validation) return undefined;
    if (field.validation.max !== undefined) return field.validation.max;
    if (field.validation.maxWhen) {
      const { max, ...condition } = field.validation.maxWhen;
      const conditionEntry = Object.entries(condition)[0] as [string, unknown];
      const currentValue =
        getByPath(previewValues, `${parentPath}.${conditionEntry[0]}`) ??
        getByPath(previewValues, conditionEntry[0]);
      if (currentValue === conditionEntry[1])
        return typeof max === "number" ? max : undefined;
    }
    return undefined;
  };

  const resolveRequired = (
    field: TemplateFieldLike,
    parentPath: string,
  ): boolean => {
    if (field.validation?.required) return true;
    if (field.validation?.requiredWhen) {
      const conditionEntry = Object.entries(field.validation.requiredWhen)[0] as [
        string,
        unknown,
      ];
      const dependencyKey = conditionEntry[0];
      const expectedValue = conditionEntry[1];
      const currentValue =
        getByPath(previewValues, `${parentPath}.${dependencyKey}`) ??
        getByPath(previewValues, dependencyKey) ??
        (() => {
          for (const step of steps) {
            const valueInStep = getByPath(previewValues, `${step.stepId}.${dependencyKey}`);
            if (valueInStep !== undefined) return valueInStep;
          }
          return undefined;
        })();
      if (Array.isArray(expectedValue)) return expectedValue.includes(currentValue);
      return currentValue === expectedValue;
    }
    return false;
  };

  // Backend rule: a percentage component must resolve to at least the minimum amount.
  const isBelowMinResolvedAmount = (
    field: TemplateFieldLike,
    rawValue: unknown,
    parentPath: string,
  ): boolean => {
    const minResolvedAmount = field.validation?.minResolvedAmount;
    if (!minResolvedAmount) return false;
    if (rawValue === undefined || rawValue === null || rawValue === "") return false;

    if (
      minResolvedAmount.appliesWhen &&
      !evaluateVisibleWhen(minResolvedAmount.appliesWhen, parentPath)
    ) {
      return false;
    }

    const baseAmount = Number(getByPath(previewValues, "core.baseAmount")) || 0;
    const resolvedAmount = (Number(rawValue) / 100) * baseAmount;

    return resolvedAmount < minResolvedAmount.amount;
  };

  const isFieldValueValid = (
    field: TemplateFieldLike,
    fullPath: string,
  ): boolean => {
      const parentPath = fullPath.replace(`.${field.key}`, "");
      if (!isFieldVisible(field, parentPath)) return true;
      const raw = getByPath(previewValues, fullPath);

      if (field.type === "file_upload" && uploadingFields[fullPath]) {
        return false;
      }

      if (isGroupLike(field)) {
        const nestedValue = raw ?? {};
        if (field.validation?.required && Object.keys(nestedValue).length === 0)
          return false;
        for (const childField of field.fields || []) {
          if (!isFieldValueValid(childField, `${fullPath}.${childField.key}`))
            return false;
        }
        return true;
      }

      const isFieldRequired = resolveRequired(field, parentPath);
      if (isFieldRequired) {
        if (field.type === "multiselect") {
          if (!Array.isArray(raw) || raw.length === 0) return false;
        } else {
          if (raw === undefined || raw === null || raw === "") return false;
        }
      }

      if (
        (field.type === "number" || field.type === "duration") &&
        raw !== undefined &&
        raw !== ""
      ) {
        const numericValue = Number(raw);
        if (isNaN(numericValue)) return false;
        if (field.validation?.min !== undefined && numericValue < field.validation.min)
          return false;
        const maxValue = resolveMax(field, parentPath);
        if (maxValue !== undefined && numericValue > maxValue) return false;
        if (isBelowMinResolvedAmount(field, numericValue, parentPath)) return false;
      }

      if (
        field.type === "date" &&
        raw &&
        !isFourDigitYearDate(raw)
      ) {
        return false;
      }

      if (field.type === "date" && raw) {
        const lockedDate = getEventDefaultForField(field.key, eventDefaults);
        if (lockedDate === undefined || lockedDate !== raw) {
          const dateBounds = resolveDateFieldBounds(field, {
            formData: previewValues,
            parentPath,
          });
          if (dateBounds.min && raw < dateBounds.min) return false;
          if (dateBounds.max && raw > dateBounds.max) return false;
        }
      }

      const uniqueContext = getUniqueAcrossItemsContextFromPath(
        previewValues,
        fullPath,
        field.key,
      );
      if (
        getUniqueAcrossItemsError(
          field,
          raw,
          uniqueContext.items,
          uniqueContext.currentIndex,
        )
      ) {
        return false;
      }

      return true;
  };

  const getPayoutComponentsWithoutPolicy = (
    step: TemplateStepLike,
  ): string[] => {
      if (!step?.outcomeRegistry || !step?.stepId) return [];

      const registryRows = getOutcomeRegistryRows(step.outcomeRegistry);
      const availableComponents = new Set<string>();
      const appliedComponents = new Set<string>();

      registryRows.forEach((row) => {
        if (row.optionsFrom) {
          resolveOptionsFrom(row.optionsFrom).forEach((option) => {
            if (option.value) {
              availableComponents.add(String(option.value));
            }
          });
        }

        const policyValue = getOutcomePolicyFormValue(
          row,
          getByPath(previewValues, `${step.stepId}.outcomes.${row.key}`),
        );

        policyValue.selectedComponents.forEach((component) => {
          appliedComponents.add(String(component));
        });
      });

      return Array.from(availableComponents).filter(
        (component) => !appliedComponents.has(component),
      );
    };

  const validateStep = (index: number): boolean => {
      const step = steps[index];
      if (!step) return true;

      if (
        step.outcomeRegistry &&
        getPayoutComponentsWithoutPolicy(step).length > 0
      ) {
        return false;
      }

      if (step.repeatable) {
        const rows = (getByPath(previewValues, step.stepId) as unknown[]) || [];

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
          for (const field of step.fields || []) {
            if (!isFieldValueValid(field, `${step.stepId}[${rowIndex}].${field.key}`)) {
              return false;
            }
          }
        }

        return true;
      }

      for (const field of step.fields || []) {
        if (!isFieldValueValid(field, `${step.stepId}.${field.key}`)) {
          return false;
        }
      }

      return true;
    };

  const validateAll = (): boolean => {
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) if (!validateStep(stepIndex)) return false;
    return true;
  };

  const isRequiredValueMissing = (
    field: TemplateFieldLike,
    fullPath: string,
    parentPath: string,
  ): boolean => {
      if (!isFieldVisible(field, parentPath)) return false;
      if (!resolveRequired(field, parentPath)) return false;

      const raw = getByPath(previewValues, fullPath);
      if (field.type === "multiselect") {
        return !Array.isArray(raw) || raw.length === 0;
      }
      return raw === undefined || raw === null || raw === "";
    };

  const getDynamicFieldLabel = (field: TemplateFieldLike): string => {
      const rawLabel =
        (field?.label && translateFieldText(field.label)) ||
        (field?.title && translateFieldText(field.title)) ||
        (field?.name && translateFieldText(field.name)) ||
        String(field?.key || "")
          .split(".")
          .pop();

      const label = String(rawLabel || "Field")
        .replace(/\s*\*+\s*$/g, "")
        .trim();

      if (field?.label || field?.title || field?.name) return label;

      return label
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getRequiredFieldMessage = (field: TemplateFieldLike): string => {
      const fieldLabel = getDynamicFieldLabel(field);
      const fieldType = String(field?.type || "").toLowerCase();
      const fieldKey = String(field?.key || "").toLowerCase();

      if (fieldType === "file_upload") {
        return t("requiredUploadField", { field: fieldLabel });
      }

      if (
        fieldType === "select" ||
        fieldType === "enum" ||
        fieldType === "multiselect" ||
        fieldType === "fighter_search" ||
        fieldType === "date" ||
        fieldKey === "country" ||
        fieldKey === "city"
      ) {
        return t("requiredSelectField", { field: fieldLabel });
      }

      if (fieldType === "checkbox" || fieldType === "boolean") {
        return t("requiredChooseField", { field: fieldLabel });
      }

      return t("requiredEnterField", { field: fieldLabel });
    };

  const collectRequiredFieldIssues = (): RequiredFieldIssue[] => {
    const issues: RequiredFieldIssue[] = [];

    const getLabel = (field: TemplateFieldLike, parentLabel?: string) => {
      const label = getDynamicFieldLabel(field);
      return parentLabel ? `${parentLabel} - ${label}` : label;
    };

    const collectField = (
      field: TemplateFieldLike,
      fullPath: string,
      parentPath: string,
      stepIndex: number,
      stepTitle: string,
      parentLabel?: string,
    ) => {
      if (!isFieldVisible(field, parentPath)) return;

      const label = getLabel(field, parentLabel);

      if ((isGroupLike(field) || field.type === "group") && field.repeatable) {
        const items = getByPath(previewValues, fullPath);
        if (
          resolveRequired(field, parentPath) &&
          (!Array.isArray(items) || items.length === 0)
        ) {
          issues.push({ stepIndex, stepTitle, label });
          return;
        }

        if (Array.isArray(items)) {
          items.forEach((_, itemIndex) => {
            const itemPath = `${fullPath}[${itemIndex}]`;
            (field.fields || []).forEach((childField) => {
              collectField(
                childField,
                `${itemPath}.${childField.key}`,
                itemPath,
                stepIndex,
                stepTitle,
                `${label} #${itemIndex + 1}`,
              );
            });
          });
        }
        return;
      }

      if (isGroupLike(field) || field.type === "group") {
        const visibleChildren = (field.fields || []).filter((childField) =>
          isFieldVisible(childField, fullPath),
        );

        if (
          visibleChildren.length === 0 &&
          isRequiredValueMissing(field, fullPath, parentPath)
        ) {
          issues.push({ stepIndex, stepTitle, label });
          return;
        }

        visibleChildren.forEach((childField) => {
          collectField(
            childField,
            `${fullPath}.${childField.key}`,
            fullPath,
            stepIndex,
            stepTitle,
            label,
          );
        });
        return;
      }

      if (isRequiredValueMissing(field, fullPath, parentPath)) {
        issues.push({ stepIndex, stepTitle, label });
      }
    };

    steps.forEach((step, stepIndex) => {
      const stepTitle = String(step?.title || `Step ${stepIndex + 1}`);

      if (step.repeatable) {
        const items = getByPath(previewValues, step.stepId);
        if (Array.isArray(items)) {
          items.forEach((_, itemIndex) => {
            const parentPath = `${step.stepId}[${itemIndex}]`;
            (step.fields || []).forEach((field) => {
              collectField(
                field,
                `${parentPath}.${field.key}`,
                parentPath,
                stepIndex,
                stepTitle,
              );
            });
          });
        }
        return;
      }

      (step.fields || []).forEach((field) => {
        collectField(
          field,
          `${step.stepId}.${field.key}`,
          step.stepId,
          stepIndex,
          stepTitle,
        );
      });
    });

    return issues;
  };

  const renderPrimitive = (
    field: TemplateFieldLike,
    fullPath: string,
  ) => {
      const value = getByPath<string>(previewValues, fullPath) ?? "";
      const parentPath = fullPath.replace(`.${field.key}`, "");
      const isPayoutField = /payout/i.test(fullPath);
      const lockedEventValue = getEventDefaultForField(field.key, eventDefaults);
      const hasFieldError =
        isErrorShown(fullPath) && !isFieldValueValid(field, fullPath);

      if (isGroupLike(field) || field.type === "group") {
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {field.fields?.map((childField) => {
              const childPath = `${fullPath}.${childField.key}`;
              const showRequiredError =
                isErrorShown(childPath) &&
                isRequiredValueMissing(childField, childPath, fullPath);

              return (
                <div key={childField.key}>
                  <LabelRow
                    label={translateFieldText(childField.label)}
                    required={resolveRequired(childField, fullPath)}
                    type={childField.type}
                  />
                  {renderPrimitive(childField, childPath)}
                  {showRequiredError && (
                    <p className="mt-1 text-[10px] font-medium text-red-500">
                      {getRequiredFieldMessage(childField)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      const fieldWidthClass = isPayoutField
        ? "w-full md:max-w-[260px]"
        : "w-full";
      const inputStateClass = hasFieldError
        ? "border-red-500 ring-1 ring-red-200 focus:border-red-500 focus:ring-red-100"
        : "border-[#E4E4E7] focus:border-[#0080FF] focus:ring-2 focus:ring-[#0080FF]/20";

      const baseInput = `${fieldWidthClass} h-10 border rounded-2xl px-3.5 text-sm text-[#11181C] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] focus:outline-none ${inputStateClass} transition placeholder-gray-400`;
      const lockedInput = `${fieldWidthClass} h-10 border border-gray-200 rounded-2xl px-3.5 text-sm text-gray-700 bg-gray-50 cursor-not-allowed shadow-[0px_1px_2px_rgba(0,0,0,0.05)]`;

      switch (field.type) {
        case "fighter_search": {
          const storeSelectedAs: "id" | "name" = field.storeSelectedAs === "name" ? "name" : "id";
          const nameKey =
            field.nameField ||
            (String(field.key).toLowerCase().endsWith("id")
              ? String(field.key).replace(/id$/i, "Name")
              : field.key);
          const namePath = (() => {
            const namePathParts = fullPath.split(".");
            namePathParts[namePathParts.length - 1] = nameKey;
            return namePathParts.join(".");
          })();
          const storedFighterName =
            (getByPath<string>(previewValues, namePath) ??
              (storeSelectedAs === "name" ? value : "")) ?? "";

          return (
            <div className={fieldWidthClass}>
              <FighterSearchInput
                value={value ?? ""}
                displayName={storedFighterName}
                displayField={field.displayField}
                valueField={field.valueField}
                allowManualEntry={field.allowManualEntry}
                storeSelectedAs={storeSelectedAs}
                placeholder={translateFieldText(field.placeholder)}
                isInvalid={hasFieldError}
                onChange={(fighterId, fighterName) => {
                  updateValue(fullPath, fighterId, field.onChange);
                  if (namePath !== fullPath) {
                    updateValue(namePath, fighterName);
                  }
                }}
              />
            </div>
          );
        }

        case "duration": {
          const maxVal = resolveMax(field, parentPath);
          const numVal = Number(value);
          const isOverMax =
            maxVal !== undefined &&
            value !== "" &&
            !isNaN(numVal) &&
            numVal > maxVal;

          return (
            <div className={fieldWidthClass}>
              <DurationInput
                value={value ?? ""}
                unit={field.unit ?? "minutes"}
                min={field.validation?.min}
                max={maxVal}
                isInvalid={hasFieldError || isOverMax}
                onChange={(val) => updateValue(fullPath, val, field.onChange)}
              />
              {isOverMax && field.validation?.maxMessage && (
                <p className="text-[10px] text-red-500 mt-1">
                  {translateFieldText(field.validation.maxMessage)}
                </p>
              )}
            </div>
          );
        }

        case "text":
        case "string":
          if (lockedEventValue !== undefined) {
            return (
              <input
                className={lockedInput}
                value={lockedEventValue}
                disabled
                readOnly
              />
            );
          }

          if (field.key === "country") {
            return (
              <div className={fieldWidthClass}>
                <CountryDropdown
                  value={value ?? ""}
                  isInvalid={hasFieldError}
                  onChange={(val) => updateValue(fullPath, val, field.onChange)}
                />
              </div>
            );
          }
          if (field.key === "city") {
            return (
              <div className={fieldWidthClass}>
                <CityDropdown
                  value={value ?? ""}
                  country={resolveCountryForCity(parentPath)}
                  isInvalid={hasFieldError}
                  onChange={(val) => updateValue(fullPath, val, field.onChange)}
                />
              </div>
            );
          }
          return (
            <input
              className={baseInput}
              aria-invalid={hasFieldError || undefined}
              placeholder={
                field.placeholder
                  ? translateFieldText(field.placeholder)
                  : t("enterField", { field: translateFieldText(field.label) })
              }
              value={value ?? ""}
              onChange={(event) =>
                updateValue(fullPath, event.target.value, field.onChange)
              }
            />
          );

        case "number": {
          const maxVal = resolveMax(field, parentPath);
          const mode = getByPath<string>(previewValues, `${parentPath}.mode`);
          const baseAmount =
            Number(
              (previewValues?.core as Record<string, unknown> | undefined)
                ?.baseAmount,
            ) || 0;
          const calculatedAmount = getComponentAmount({ value, mode }, baseAmount);

          return (
            <>
              <input
                className={baseInput}
                type="number"
                aria-invalid={hasFieldError || undefined}
                placeholder={
                  field.placeholder
                    ? translateFieldText(field.placeholder)
                    : t("enterField", { field: translateFieldText(field.label) })
                }
                value={value ?? ""}
                min={field.validation?.min}
                max={maxVal}
                onChange={(event) =>
                  updateValue(
                    fullPath,
                    event.target.value === "" ? "" : Number(event.target.value),
                    field.onChange,
                  )
                }
              />
              {field.helperText && (
                <p className="text-[10px] text-blue-500 mt-1 leading-tight">
                  {translateFieldText(field.helperText)}
                </p>
              )}
              {maxVal !== undefined && field.validation?.maxWhen && (
                <p className="text-[10px] text-amber-500 mt-1">
                  {t("maxAllowed", { max: maxVal })}
                </p>
              )}
              {mode === PERCENT_OF_BASE_MODE && (
                <>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {t("finalAmount", {
                      amount: calculatedAmount.toFixed(2),
                    })}
                  </p>
                  {isBelowMinResolvedAmount(field, value, parentPath) && (
                    <p className="text-[10px] text-red-500 mt-1">
                      {t("minimumPayoutMessage")}
                    </p>
                  )}
                </>
              )}
            </>
          );
        }

        case "date": {
          const dateBounds = resolveDateFieldBounds(field, {
            formData: previewValues,
            parentPath,
          });
          const lockedDateValue =
            lockedEventValue !== undefined ? lockedEventValue : undefined;
          const dateValue =
            lockedDateValue !== undefined
              ? lockedDateValue
              : isFourDigitYearDate(value)
                ? value ?? ""
                : "";

          if (lockedDateValue !== undefined) {
            return (
              <input
                className={`${lockedInput} [color-scheme:light]`}
                type="date"
                value={dateValue}
                disabled
                readOnly
              />
            );
          }

          return (
            <input
              className={`${baseInput} [color-scheme:light]`}
              type="date"
              aria-invalid={hasFieldError || undefined}
              value={dateValue}
              min={dateBounds.min}
              max={dateBounds.max ?? DATE_MAX_FOUR_DIGIT_YEAR}
              onChange={(event) => {
                const nextValue = event.target.value;
                if (!isFourDigitYearDate(nextValue)) {
                  event.currentTarget.value = String(dateValue);
                  return;
                }
                updateValue(fullPath, nextValue, field.onChange);
              }}
            />
          );
        }

        case "file_upload": {
          const isUploading = !!uploadingFields[fullPath];
          const fileVal = getByPath(
            previewValues,
            fullPath,
          ) as
            | string
            | { fileUrl?: string; url?: string; fileName?: string; name?: string }
            | undefined;
          const fileUrl =
            typeof fileVal === "string"
              ? fileVal
              : fileVal?.fileUrl || fileVal?.url || "";
          const fileName =
            uploadedFileNames[fullPath] ||
            (typeof fileVal === "object"
              ? fileVal?.fileName || fileVal?.name
              : "") ||
            (fileUrl ? fileUrl.split("/").pop()?.split("?")[0] : "");
          const maxSizeMB = Number(field.validation?.maxSizeMB || 0);
          const rawAllowedTypes = field.validation?.allowedTypes;
          const allowedTypes = Array.isArray(rawAllowedTypes)
            ? rawAllowedTypes
            : [];
          const acceptedExtensions = String(field.accept || "")
            .split(",")
            .map((part) => part.trim().toLowerCase())
            .filter((part) => part.startsWith("."));

          const handleFileChange = async (
            event: React.ChangeEvent<HTMLInputElement>,
          ) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;

            if (maxSizeMB > 0 && file.size > maxSizeMB * 1024 * 1024) {
              toast.error(t("fileSizeLimit", { size: maxSizeMB }));
              return;
            }

            const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
            const typeAllowed =
              allowedTypes.length === 0 ||
              allowedTypes.includes(file.type) ||
              acceptedExtensions.includes(fileExtension);
            if (!typeAllowed) {
              toast.error(t("unsupportedFileType"));
              return;
            }

            setUploadingFields((prev) => ({ ...prev, [fullPath]: true }));
            setUploadedFileNames((prev) => ({
              ...prev,
              [fullPath]: file.name,
            }));
            try {
              const uploadedUrl = await uploadMediaFile(file);
              const storedValue =
                field.storageType === "link"
                  ? uploadedUrl
                  : {
                    fileName: file.name,
                    fileUrl: uploadedUrl,
                    fileType: file.type,
                    fileSize: file.size,
                  };
              updateValue(fullPath, storedValue, field.onChange);
            } catch (err: unknown) {
              const axiosErr = err as AxiosError<{ message?: string }> | undefined;
              setUploadedFileNames((prev) => {
                const next = { ...prev };
                delete next[fullPath];
                return next;
              });
              updateValue(fullPath, null, field.onChange);
              toast.error(
                axiosErr?.response?.data?.message ||
                axiosErr?.message ||
                t("fileUploadFailed"),
              );
            } finally {
              setUploadingFields((prev) => {
                const next = { ...prev };
                delete next[fullPath];
                return next;
              });
            }
          };

          return (
            <div className={`${fieldWidthClass} space-y-2`}>
              <label
                className={`${fieldWidthClass} flex h-9 items-center overflow-hidden rounded-md border bg-white text-xs transition ${isUploading
                    ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                    : hasFieldError
                      ? "cursor-pointer border-red-500 text-gray-800 ring-1 ring-red-200 hover:border-red-500"
                      : "cursor-pointer border-gray-300 text-gray-800 hover:border-blue-400"
                  }`}
              >
                <span className="flex h-full flex-shrink-0 items-center border-r border-gray-200 bg-blue-50 px-3 font-semibold text-blue-700">
                  {t("chooseFile")}
                </span>
                <span
                  className={`min-w-0 flex-1 truncate px-3 ${fileName ? "text-gray-700" : "text-gray-400"
                    }`}
                  title={fileName || t("noFileChosen")}
                >
                  {fileName || t("noFileChosen")}
                </span>
                <input
                  className="sr-only"
                  type="file"
                  accept={field.accept}
                  disabled={isUploading}
                  onChange={handleFileChange}
                />
              </label>
              <div className="min-h-[18px]">
                {isUploading ? (
                  <p className="text-[10px] text-blue-500">
                    {t("uploadingFile")}
                  </p>
                ) : fileUrl ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 truncate text-[11px] font-medium text-blue-600 hover:text-blue-700"
                      title={fileName || fileUrl}
                    >
                      {fileName || t("viewUploadedFile")}
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        updateValue(fullPath, null, field.onChange);
                        setUploadedFileNames((prev) => {
                          const next = { ...prev };
                          delete next[fullPath];
                          return next;
                        });
                      }}
                      className="flex-shrink-0 text-[10px] font-semibold text-red-500 hover:text-red-600"
                    >
                      {t("removeFile")}
                    </button>
                  </div>
                ) : field.placeholder ? (
                  <p className="text-[10px] text-gray-400">
                    {translateFieldText(field.placeholder)}
                  </p>
                ) : null}
              </div>
            </div>
          );
        }

        case "checkbox":
        case "boolean":
          return (
            <div className="flex items-center w-full h-10 border border-[#E4E4E7] hover:border-gray-300 rounded-2xl px-3.5 bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors">
              <label className="flex items-center gap-2.5 cursor-pointer select-none w-full h-full">
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(event) =>
                    updateValue(fullPath, event.target.checked, field.onChange)
                  }
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer accent-[#0080FF]"
                />
                <span className="text-xs font-medium text-[#11181C]">
                  {translateFieldText(field.label)}
                </span>
              </label>
            </div>
          );

        case "select":
        case "enum": {
          const rawOpts = getOptionsForField(field) as Array<SelectOption & { visibleWhen?: Record<string, unknown> }>;
          const opts = rawOpts.filter((opt) => {
            if (!opt.visibleWhen) return true;
            return evaluateVisibleWhen(opt.visibleWhen, parentPath);
          });
          const validValues = opts.map((option) => option.value);
          // Options that depend on another field (visibleWhen) are empty until that field is chosen.
          const isDependentOptionsEmpty =
            opts.length === 0 && rawOpts.some((opt) => opt.visibleWhen);
          const lockedSelectValue =
            lockedEventValue !== undefined ? lockedEventValue : undefined;
          const displayValue =
            lockedSelectValue !== undefined
              ? lockedSelectValue
              : value && validValues.includes(value)
                ? value
                : "";
          const isOptionsLoading = isFieldOptionsLoading(field);
          const uniqueContext = getUniqueAcrossItemsContextFromPath(
            previewValues,
            fullPath,
            field.key,
          );
          const disabledUniqueValues = getDisabledUniqueAcrossItemsValues(
            field,
            uniqueContext.items,
            uniqueContext.currentIndex,
          );
          const uniqueError = getUniqueAcrossItemsError(
            field,
            displayValue,
            uniqueContext.items,
            uniqueContext.currentIndex,
          );

          if (lockedSelectValue !== undefined) {
            return (
              <input
                className={lockedInput}
                value={lockedSelectValue}
                disabled
                readOnly
              />
            );
          }

          return (
            <>
              <AppSelect
                aria-label={translateFieldText(field.label)}
                variant="bordered"
                className={fieldWidthClass}
                placeholder={
                  isOptionsLoading
                    ? t("loading")
                    : t("selectField", { field: translateFieldText(field.label) })
                }
                isDisabled={isOptionsLoading || isDependentOptionsEmpty}
                selectedKeys={displayValue !== "" ? [String(displayValue)] : []}
                disabledKeys={opts
                  .filter(
                    (opt) =>
                      opt.value !== displayValue &&
                      disabledUniqueValues.has(String(opt.value)),
                  )
                  .map((opt) => String(opt.value))}
                onSelectionChange={(keys) => {
                  const next = keys instanceof Set ? Array.from(keys)[0] : undefined;
                  updateValue(
                    fullPath,
                    next === undefined ? "" : String(next),
                    field.onChange,
                  );
                }}
                classNames={{
                  trigger: `h-10 min-h-10 rounded-2xl border-small px-3.5 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] ${
                    hasFieldError
                      ? "border-red-500 data-[hover=true]:border-red-500 data-[focus=true]:border-red-500 data-[open=true]:border-red-500"
                      : "border-[#E4E4E7] data-[hover=true]:border-[#0080FF] data-[focus=true]:border-[#0080FF] data-[open=true]:border-[#0080FF]"
                  }`,
                  value: "text-sm text-[#11181C]",
                }}
              >
                {opts.map((opt) => (
                  <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                ))}
              </AppSelect>
              {hasFieldError && uniqueError && (
                <p className="mt-1 text-[10px] font-medium text-red-500">
                  {translateFieldText(uniqueError)}
                </p>
              )}
            </>
          );
        }

        case "multiselect": {
          let opts: SelectOption[] = [];
          if (field.optionsFrom) {
            opts = formatOptionLabelsForUi(
              resolveOptionsFrom(field.optionsFrom),
            ) as SelectOption[];
          } else {
            opts = getOptionsForField(field);
          }
          const rows = getByPath<unknown[]>(previewValues, fullPath);
          const currentArr = (Array.isArray(rows) ? rows : []).filter(
            (optionValue: unknown) =>
              opts
                .map((option) => String(option.value))
                .includes(String(optionValue)),
          );
          return (
            <div className={fieldWidthClass}>
              <MultiSelectDropdown
                value={currentArr as string[]}
                options={opts.map((option) => ({ label: option.label, value: String(option.value) }))}
                isInvalid={hasFieldError}
                placeholder={t("selectFieldWithEllipsis", {
                  field: translateFieldText(field.label),
                })}
                onChange={(val) => updateValue(fullPath, val, field.onChange)}
              />
            </div>
          );
        }

        case "textarea":
          return (
            <textarea
              value={value ?? ""}
              placeholder={
                field.placeholder
                  ? translateFieldText(field.placeholder)
                  : t("enterField", { field: translateFieldText(field.label) })
              }
              rows={field.rows || 3}
              onChange={(event) =>
                updateValue(fullPath, event.target.value, field.onChange)
              }
              aria-invalid={hasFieldError || undefined}
              className={`w-full col-span-full border border-[#E4E4E7] rounded-2xl p-3.5 text-sm text-[#11181C] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] focus:outline-none ${inputStateClass} transition placeholder-gray-400 resize-none`}
            />
          );

        default:
          return (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-center gap-2">
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
              {t("unsupportedFieldType", { type: String(field.type) })}
              {field.label ? (
                <strong className="font-bold">({translateFieldText(field.label)})</strong>
              ) : null}
            </div>
          );
      }
    };

  const renderFieldWithLabel = (
    field: TemplateFieldLike,
    fullPath: string,
    parentPath: string,
  ) => {
      const showRequiredError =
        isErrorShown(fullPath) &&
        isRequiredValueMissing(field, fullPath, parentPath);

      if (
        field.type === "checkbox" ||
        field.type === "boolean"
      ) {
        return (
          <div className="flex flex-col">
            <div className="flex flex-col mb-1.5 min-h-[17px] justify-end">
              <span className="invisible text-xs font-medium leading-tight select-none">
                {translateFieldText(field.label)}
              </span>
            </div>
            <div className="mt-1">
              {renderPrimitive(field, fullPath)}
            </div>
            {showRequiredError && (
              <p className="mt-1 text-[10px] font-medium text-red-500">
                {getRequiredFieldMessage(field)}
              </p>
            )}
          </div>
        );
      }

      return (
        <>
          <LabelRow
            label={translateFieldText(field.label)}
            required={resolveRequired(field, parentPath)}
            type={field.type}
          />
          <div className="mt-1">{renderPrimitive(field, fullPath)}</div>
          {showRequiredError && (
            <p className="mt-1 text-[10px] font-medium text-red-500">
              {getRequiredFieldMessage(field)}
            </p>
          )}
        </>
      );
    };

  const getDisplay = (field: TemplateFieldLike, val: unknown): string => {
      if (val === undefined || val === null || val === "") return "-";
      if (typeof val === "boolean") return val ? t("yes") : t("no");
      if (field.type === "duration") {
        const unit =
          field.unit === "minutes"
            ? "min"
            : field.unit === "seconds"
              ? "sec"
              : field.unit === "hours"
                ? "hr"
                : (field.unit ?? "");
        return `${val} ${unit}`;
      }
      if (field.type === "multiselect") {
        const arr = Array.isArray(val) ? val : [];
        if (arr.length === 0) return "-";
        const opts = getOptionsForField(field);
        return arr
          .map(
            (optionValue: string) =>
              opts.find((option) => option.value === optionValue)?.label ??
              formatOptionLabelForUi(optionValue),
          )
          .join(", ");
      }
      if (field.type === "file_upload") {
        if (typeof val === "object") {
          const fileObj = val as { fileName?: string; fileUrl?: string };
          return fileObj.fileName || fileObj.fileUrl || t("uploadedFile");
        }
        const fileName = String(val).split("/").pop()?.split("?")[0];
        return fileName || t("uploadedFile");
      }
      if (field.type === "select" || field.type === "enum") {
        const opts = getOptionsForField(field);
        const matched = opts.find((option) => option.value === String(val));
        return matched ? matched.label : formatOptionLabelForUi(val);
      }
      return String(val);
    };

  const buildReviewSummary = (): ReviewSummaryStep[] => {
    return steps.map((step) => {
      const stepValues = getByPath(previewValues, step.stepId);
      const stepFields = Array.isArray(step.fields) ? step.fields : [];
      let fields: ReviewSummaryField[] = [];
      if (step.repeatable) {
        if (Array.isArray(stepValues)) {
          stepValues.forEach((item, index) => {
            stepFields
              .filter((field) => isFieldVisible(field, `${step.stepId}[${index}]`))
              .forEach((field) => {
                fields.push({
                  label: `${translateFieldText(step.title)} #${index + 1} - ${translateFieldText(field.label)}`,
                  display: getDisplay(field, getByPath(item, field.key)),
                  required: resolveRequired(field, `${step.stepId}[${index}]`),
                });
              });
          });
        }
      } else {
        const context =
          (stepValues as Record<string, unknown> | undefined) || {};
        fields = stepFields
          .filter((field) => isFieldVisible(field, step.stepId))
          .map((field) => {
            if (isGroupLike(field) || field.type === "group") {
              const nested =
                (getByPath<Record<string, unknown>>(context, field.key) || {}) as
                  | Record<string, unknown>
                  | undefined;
              return (field.fields ?? []).map((child) => ({
                label: `${translateFieldText(field.label)} - ${translateFieldText(child.label)}`,
                display: getDisplay(
                  child,
                  getByPath<unknown>(nested, child.key),
                ),
                required: resolveRequired(child, `${step.stepId}.${field.key}`),
              }));
            }
            return [
              {
                label: translateFieldText(field.label),
                display: getDisplay(field, getByPath(context, field.key)),
                required: resolveRequired(field, step.stepId),
              },
            ];
          })
          .flat();
      }
      return { title: translateFieldText(step.title), stepId: step.stepId, fields };
    });
  };

  const payoutComponents: PayoutComponent[] = (() => {
    const repeatableStepId = steps.find((step) => step.repeatable)?.stepId;
    if (!repeatableStepId) return [];
    const items = getByPath(previewValues, repeatableStepId);
    return Array.isArray(items) ? (items as PayoutComponent[]) : [];
  })();

  const currentStep = steps[currentStepIndex] || null;

  const isLastStep = currentStepIndex === steps.length - 1;

  const currentPayoutPolicyErrors =
    currentStep?.outcomeRegistry &&
      attemptedStepIndexes.has(currentStepIndex)
      ? getPayoutComponentsWithoutPolicy(currentStep)
      : [];

  const payoutPolicyErrorMessage =
    currentPayoutPolicyErrors.length > 0
      ? t("payoutPolicy.componentsMustBeApplied", {
        components: currentPayoutPolicyErrors
          .map(formatOptionLabelForUi)
          .join(", "),
      })
      : "";

  const allStepsValid = validateAll();

  const nextStep = () => {
    if (!validateStep(currentStepIndex)) {
      setAttemptedStepIndexes((prev) => {
        const next = new Set(prev);
        next.add(currentStepIndex);
        return next;
      });

      return;
    }

    setCurrentStepIndex((index) => Math.min(index + 1, steps.length - 1));
  };

  const prevStep = () => setCurrentStepIndex((index) => Math.max(index - 1, 0));

  const handleSubmitClick = async () => {
    if (!allStepsValid) {
      const requiredIssues = collectRequiredFieldIssues();
      setAttemptedStepIndexes(new Set(steps.map((_, index: number) => index)));
      if (requiredIssues[0]) {
        setCurrentStepIndex(requiredIssues[0].stepIndex);
      }
      return;
    }
    await onSubmit();
  };

  const renderStepContent = () => {
    if (!currentStep) return null;

    if (currentStep.outcomeRegistry) {
      return (
        <OutcomeRegistryStep
          step={currentStep}
          outcomeRegistry={currentStep.outcomeRegistry}
          previewValues={previewValues}
          payoutComponents={payoutComponents}
          updateValue={updateValue}
          resolveOptionsFrom={resolveOptionsFrom}
          translateFieldText={translateFieldText}
        />
      );
    }

    if ((currentStep.fields || []).length === 0) {
      return (
        <ReviewStep
          allStepsValid={allStepsValid}
          isEditMode={isEditMode}
          summaries={buildReviewSummary()}
          t={t}
        />
      );
    }

    if (currentStep.repeatable) {
      return (
        <RepeatableStep
          step={currentStep}
          previewValues={previewValues}
          setPreviewValues={setPreviewValues}
          renderFieldWithLabel={renderFieldWithLabel}
          isErrorShown={isErrorShown}
          isFieldValueValid={isFieldValueValid}
          isFieldVisible={isFieldVisible}
          resetAttemptedForCurrentStep={resetAttemptedForCurrentStep}
          clearTouchedUnder={clearTouchedUnder}
          translateFieldText={translateFieldText}
          t={t}
        />
      );
    }

    const normalizeKey = (value: string) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[_\s-]+/g, "");

    const POLICY_SUFFIXES = ["payouttype", "policy", "type"];
    const COMPONENT_SUFFIXES = ["components"];

    const getPrefix = (key: string): string => {
      const lower = normalizeKey(key);
      const allSuffixes = [...POLICY_SUFFIXES, ...COMPONENT_SUFFIXES];
      for (const suffix of allSuffixes) {
        if (lower.endsWith(suffix))
          return lower.slice(0, lower.length - suffix.length);
      }
      return lower;
    };

    const isPolicyField = (key: string) => {
      const lower = normalizeKey(key);
      return POLICY_SUFFIXES.some((suffix) => lower.endsWith(suffix));
    };

    const isComponentField = (key: string) => {
      const lower = normalizeKey(key);
      return COMPONENT_SUFFIXES.some((suffix) => lower.endsWith(suffix));
    };

    const fields = currentStep.fields ?? [];
    const pairedPrefixOrder: string[] = [];
    const policyMap = new Map<string, TemplateFieldLike>();
    const componentMap = new Map<string, TemplateFieldLike>();
    const extraFields: TemplateFieldLike[] = [];

    for (const field of fields) {
      const key = String(field?.key ?? "");
      if (isPolicyField(key) || isComponentField(key)) {
        const prefix = getPrefix(key);
        if (!pairedPrefixOrder.includes(prefix)) pairedPrefixOrder.push(prefix);
        if (isPolicyField(key)) policyMap.set(prefix, field);
        if (isComponentField(key)) componentMap.set(prefix, field);
      } else {
        extraFields.push(field);
      }
    }

    const renderFieldCard = (field?: TemplateFieldLike) => {
      if (!field) return null;
      const basePath = `${currentStep.stepId}.${field.key}`;
      if (!isFieldVisible(field, currentStep.stepId)) return null;

      const isNested =
        field.repeatable || isGroupLike(field) || field.type === "group";

      return (
        <div className={isNested ? "col-span-full" : ""}>
          {isNested ? (
            <>
              <div className="border-b border-gray-100 pb-2 mb-3 pt-2">
                <h4 className="text-sm font-semibold text-[#11181C]">
                  {translateFieldText(field.label)}
                  {resolveRequired(field, currentStep.stepId) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </h4>
              </div>

              {field.repeatable ? (
                (() => {
                  const arr = getByPath<unknown[]>(previewValues, basePath) || [];
                  return (
                    <div className="space-y-3">
                      <div className="flex gap-2 mb-2">
                        <button
                          className="text-xs font-medium text-blue-600 border border-blue-300 rounded-md px-3 py-1.5 bg-blue-50"
                          onClick={() => {
                            const next = JSON.parse(
                              JSON.stringify(previewValues || {}),
                            );
                            const items = getByPath<unknown[]>(next, basePath) || [];
                            items.push({});
                            setByPath(next, basePath, items);
                            setPreviewValues(next);
                            resetAttemptedForCurrentStep();
                          }}
                        >
                          {t("addItem", { item: translateFieldText(field.label) })}
                        </button>
                        {arr.length > 0 && (
                          <button
                            className="text-xs font-medium text-red-500 border border-red-200 rounded-md px-3 py-1.5 bg-red-50"
                            onClick={() => {
                              const next = JSON.parse(
                                JSON.stringify(previewValues || {}),
                              );
                              setByPath(next, basePath, []);
                              setPreviewValues(next);
                              clearTouchedUnder(`${basePath}[`);
                            }}
                          >
                            {t("clear")}
                          </button>
                        )}
                      </div>

                      {arr.length === 0 && (
                        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center">
                          <p className="text-[12px] text-gray-400">
                            {t("noItemsAddedYet")}
                          </p>
                        </div>
                      )}

                      {arr.map((it: unknown, i: number) => (
                        <div
                          key={i}
                          className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                            {field.fields?.map((child) => (
                              <div key={child.key}>
                                {renderFieldWithLabel(
                                  child,
                                  `${basePath}[${i}].${child.key}`,
                                  `${basePath}[${i}]`,
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              ) : (
                <div>{renderPrimitive(field, basePath)}</div>
              )}
            </>
          ) : (
            renderFieldWithLabel(field, basePath, currentStep.stepId)
          )}
        </div>
      );
    };

    const renderPairedRow = (prefix: string) => {
      const leftField = policyMap.get(prefix);
      const rightField = componentMap.get(prefix);
      const leftNode = renderFieldCard(leftField);
      const rightNode = renderFieldCard(rightField);

      if (!leftNode && !rightNode) return null;

      if (leftNode && rightNode) {
        return (
          <div
            key={prefix}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start"
          >
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 min-h-[85px] flex justify-start">
              <div className="w-full md:max-w-[260px]">{leftNode}</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 min-h-[85px] flex justify-start">
              <div className="w-full md:max-w-[260px]">{rightNode}</div>
            </div>
          </div>
        );
      }

      return (
        <div key={prefix} className="grid grid-cols-1 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 min-h-[85px] flex justify-start">
            <div className="w-full md:max-w-[260px]">
              {leftNode || rightNode}
            </div>
          </div>
        </div>
      );
    };

    const usePairedLayout =
      /payout\s*policies/i.test(String(currentStep.title ?? "")) ||
      /payout/i.test(String(currentStep.stepId ?? "")) ||
      fields.some(
        (field) =>
          isPolicyField(String(field?.key ?? "")) ||
          isComponentField(String(field?.key ?? "")),
      );

    if (usePairedLayout) {
      return (
        <div className="space-y-4">
          {pairedPrefixOrder.map((prefix) => renderPairedRow(prefix))}
          {extraFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              {extraFields
                .filter((field) => isFieldVisible(field, currentStep.stepId))
                .map((field) => (
                  <div
                    key={field.key}
                    className={
                      field.repeatable ||
                        isGroupLike(field) ||
                        field.type === "group" ||
                        field.type === "textarea"
                        ? "col-span-full"
                        : ""
                    }
                  >
                    {renderFieldCard(field)}
                  </div>
                ))}
            </div>
          )}
        </div>
      );
    }

    const visibleFields = fields.filter((field) =>
      isFieldVisible(field, currentStep.stepId),
    );

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
        {visibleFields
          .filter((field) => field.type !== "textarea")
          .map((field) => (
            <div
              key={field.key}
              className={
                field.repeatable ||
                  isGroupLike(field) ||
                  field.type === "group"
                  ? "col-span-full"
                  : ""
              }
            >
              {renderFieldCard(field)}
            </div>
          ))}
        {visibleFields
          .filter((field) => field.type === "textarea")
          .map((field) => (
            <div key={field.key} className="col-span-full">
              {renderFieldCard(field)}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="bg-white font-sans flex flex-col h-full">
      <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-gray-100 flex-shrink-0 space-y-3">
        {templateName && (
          <h2 className="text-base sm:text-xl font-bold text-gray-900">{translateFieldText(templateName)}</h2>
        )}
        <div
          ref={tabsRef}
          className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {steps.map((step, index: number) => {
            const done = index < currentStepIndex;
            const active = index === currentStepIndex;
            return (
              <button
                data-step-index={index}
                key={step.stepId || index}
                onClick={() => setCurrentStepIndex(index)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all outline-none whitespace-nowrap shrink-0 ${
                  active
                    ? 'bg-[#D9EBFF] text-[#0080FF] border-transparent'
                    : done
                      ? 'bg-[#DCFCE7] text-[#16A34A] border-transparent'
                      : 'bg-white text-[#71717A] border-[#E4E4E7] hover:border-gray-300'
                }`}
              >
                {done ? (
                  <span className="w-4 h-4 rounded-full flex items-center justify-center bg-[#16A34A] text-white">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                ) : (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    active ? 'bg-[#0080FF] text-white' : 'bg-[#D9EBFF] text-[#0080FF]'
                  }`}>
                    {index + 1}
                  </span>
                )}
                {translateFieldText(step.title)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-4 py-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {currentStep && (
          <>
            <div className="mb-3">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h3 className="text-base font-bold text-gray-900">
                  {translateFieldText(currentStep.title)}
                </h3>

                {payoutPolicyErrorMessage && (
                  <p className="text-[11px] font-semibold leading-4 text-red-600">
                    {payoutPolicyErrorMessage}
                  </p>
                )}
              </div>

              {currentStep.description && (
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {translateFieldText(currentStep.description)}
                </p>
              )}
            </div>
            {renderStepContent()}
          </>
        )}

        {steps.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-3">
            <svg
              className="w-10 h-10 opacity-40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-[14px]">{t("noTemplateConfigured")}</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 sm:justify-end px-4 sm:px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
        {currentStepIndex > 0 && (
          <button
            onClick={prevStep}
            className="flex items-center justify-center gap-3 h-9 px-5 rounded-full text-sm font-medium text-[#11181C] bg-[#EBEBEC] hover:bg-[#E0E0E1] transition whitespace-nowrap shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            {t('previous')}
          </button>
        )}
        {!isLastStep ? (
          <button
            onClick={nextStep}
            className="flex items-center justify-center gap-3 h-9 px-5 rounded-full text-sm font-medium text-[#0080FF] bg-[#EBEBEC] hover:bg-[#E0E0E1] transition whitespace-nowrap flex-1 sm:flex-none"
          >
            {t('next')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        ) : (
          <button
            disabled={loading}
            onClick={handleSubmitClick}
            className={`flex-1 sm:flex-none px-5 py-2.5 rounded-full text-sm font-bold transition whitespace-nowrap ${
              !loading
                ? 'bg-[#0080FF] hover:bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading
              ? (isEditMode ? t('saving') : t('creating'))
              : (isEditMode ? t('saveChanges') : t('createOpportunity'))}
          </button>
        )}
      </div>
    </div>
  );
};

export default OpportunityForm;
